import type { Logger as DatadogLogger, LogsGlobal } from '@datadog/browser-logs'
import type { Logger as WinstonLogger } from 'winston'

import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isNil from 'lodash/fp/isNil'
import isString from 'lodash/fp/isString'
import noop from 'lodash/fp/noop'

import { env, getAppUrlInfo, isDev, isPreview } from '.'

const debugMode =
  (process.env.DEBUG ||
    process.env.NEXT_PUBLIC_DEBUG ||
    process.env.DEBUG_LOGS ||
    process.env.NEXT_PUBLIC_DEBUG_LOGS) === 'true'

const DD_LOGS_IN_DEV = process.env.DD_LOGS_IN_DEV === 'true' || debugMode

const environment = env()
const version =
  process.env.VERSION ||
  process.env.NEXT_PUBLIC_VERSION ||
  process.env.VERCEL_GIT_COMMIT_SHA ||
  process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ||
  'local'

type ExtendedWinstonLogger = {
  error: (
    message: string,
    context?: Record<string, unknown>,
    error?: Error,
  ) => ReturnType<WinstonLogger['error']>
  getContext: () => Record<string, unknown>
  setContext: (newContext: Record<string, unknown>) => void
} & Omit<WinstonLogger, 'error'>
export type Logger = DatadogLogger | ExtendedWinstonLogger

const SSRLoggers: Record<string, Logger> = {}

function initDatadog(
  datadogLogs: LogsGlobal,
  defaultMeta: Record<string, unknown>,
  gdprConsent: boolean = false,
) {
  if (
    typeof window === 'undefined' ||
    isEmpty(datadogLogs) ||
    !isFunction(datadogLogs?.init)
  ) {
    console.debug('Unable to init datadog', datadogLogs)

    throw new Error('Unable to init datadog')
  }

  datadogLogs?.init({
    clientToken:
      process.env.DATADOG_CLIENT_TOKEN ||
      process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    env: environment,
    forwardConsoleLogs: isDev() && !debugMode ? [] : 'all',
    forwardErrorsToLogs: !isDev() || debugMode,
    forwardReports: isDev() && !debugMode ? [] : 'all',
    sessionSampleRate: 100,
    site: 'datadoghq.eu',
    trackingConsent: gdprConsent ? 'granted' : 'not-granted',
    version,
  })

  const globalContext = defaultMeta ?? {
    env: environment,
    version,
  }

  if (isFunction(datadogLogs?.setGlobalContext)) {
    datadogLogs.setGlobalContext(globalContext)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } else if (isFunction((datadogLogs as any)?.setLoggerGlobalContext)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(datadogLogs as any).setLoggerGlobalContext(globalContext)
  } else {
    console.debug('Unable to set global context', datadogLogs)
  }
}

export function createLogger(
  name: string = 'default',
  context: Record<string, unknown> = {},
  gdprConsent: boolean = false,
): Logger {
  const { appName } = getAppUrlInfo()

  const DD_API_KEY = process.env.DATADOG_API_KEY
  const DD_SOURCE = process.env.DD_SERVER_SOURCE || 'nextjs-server'
  const DD_HOST =
    process.env.DD_SERVER_HOST || isDev()
      ? 'local-dev-server'
      : `${process.env.VERCEL_DEPLOYMENT_ID}_${process.env.VERCEL_GIT_COMMIT_SHA}_${process.env.VERCEL_REGION}_${process.env.VERCEL_ENV}_${process.env.VERCEL_URL}`
  const DD_SERVICE = process.env.DD_SERVICE || appName

  const defaultMeta = {
    deployment: process.env.VERCEL_DEPLOYMENT_ID,
    env: environment,
    region: process.env.VERCEL_REGION,
    sha: process.env.VERCEL_GIT_COMMIT_SHA,
    url: process.env.VERCEL_URL,
    version,
  }

  if (typeof window === 'undefined') {
    if (!isNil(SSRLoggers[name])) {
      return SSRLoggers[name]!
    }

    const { createLogger: createWinstonLogger, format, transports } =
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      require('winston')

    const ConsoleTransport = new transports.Console()
    const DatadogTransport = new transports.Http({
      host: 'http-intake.logs.datadoghq.eu',
      path: `/api/v2/logs?dd-api-key=${DD_API_KEY}&ddsource=${DD_SOURCE}&hostname=${DD_HOST}&service=${DD_SERVICE}`,
      ssl: true,
    })

    const addContextFormat = format((info: Record<string, unknown>) =>
      isDev()
        ? info
        : {
            ...info,
            ...context,
            ...defaultMeta,
          },
    )

    const logger = createWinstonLogger({
      defaultMeta: isDev() ? {} : { ...context, ...defaultMeta },
      format: format.combine(
        addContextFormat(),
        isDev() || debugMode
          ? format.prettyPrint({ colorize: true })
          : format.json(),
      ),
      level:
        process.env.LOG_LEVEL ||
        process.env.NEXT_PUBLIC_LOG_LEVEL ||
        (isDev() || debugMode ? 'debug' : 'info'),
      transports:
        isDev() && !DD_LOGS_IN_DEV
          ? [ConsoleTransport]
          : [ConsoleTransport, DatadogTransport],
    })
    const winstonLogger = logger as ExtendedWinstonLogger
    const originalError = winstonLogger.error

    winstonLogger.setContext = (newContext: Record<string, unknown>) => {
      logger.defaultMeta = {
        ...(isDev() ? {} : { ...context, ...defaultMeta }),
        ...newContext,
      }
    }
    winstonLogger.getContext = () => logger.defaultMeta
    winstonLogger.error = (
      message: string,
      logContext?: Record<string, unknown>,
      error?: Error,
    ) => {
      const actualError = isNil(error)
        ? isString(logContext?.error)
          ? new Error(logContext.error)
          : isNil(logContext?.error)
            ? new Error('Unexpected error')
            : (logContext.error as Error)
        : error

      return originalError.call(winstonLogger, message, {
        ...logContext,
        error: {
          message: actualError.message,
          stack: actualError.stack,
        },
      })
    }

    SSRLoggers[name] = winstonLogger

    return winstonLogger
  }

  let logger: {
    setHandler: (handler: string | Array<string>) => Logger
    setLevel: (level: string) => Logger
  } & Logger

  try {
    const datadogLogs =
      typeof window === 'undefined' || isNil(window.DD_LOGS)
        ? // eslint-disable-next-line @typescript-eslint/no-require-imports
          require('@datadog/browser-logs').datadogLogs
        : window.DD_LOGS

    if (isNil(datadogLogs.getInitConfiguration())) {
      initDatadog(datadogLogs, defaultMeta, gdprConsent)
    } else if (isFunction(datadogLogs.setTrackingConsent)) {
      datadogLogs.setTrackingConsent(gdprConsent ? 'granted' : 'not-granted')
    }

    logger = datadogLogs.getLogger(name)

    if (!isNil(logger)) {
      return logger
    }

    logger = datadogLogs.createLogger(name)

    if (!isEmpty(context)) {
      logger.setContext(context)
    }

    if (isDev() && !DD_LOGS_IN_DEV) {
      logger.setHandler('console')
      logger.setLevel('debug')
    } else if (isPreview() || DD_LOGS_IN_DEV) {
      logger.setHandler(['http', 'console'])
      logger.setLevel(isDev() || debugMode ? 'debug' : 'info')
    } else {
      logger.setHandler(['http', 'console'])
      logger.setLevel('warn')
    }
  } catch (error) {
    const actualError = error as Error
    console.error(
      `Unable to create datadog logger: "${actualError.message}"`,
      actualError,
    )

    logger = {
      alert: console.warn,
      emerg: console.error,
      getContext: () => ({}),
      notice: console.info,
      setContext: noop,
      setHandler: noop,
      setLevel: noop,
      ...console,
    } as unknown as typeof logger
  }

  const originalError = logger.error

  logger.error = (
    message: string,
    logContext?: Record<string, unknown>,
    error?: Error,
  ) => {
    const actualError = isNil(error)
      ? isString(logContext?.error)
        ? new Error(logContext.error)
        : isNil(logContext?.error)
          ? new Error('Unexpected error')
          : (logContext.error as Error)
      : error

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - Nik
    return originalError.call(logger as DatadogLogger, message, {
      ...logContext,
      error: {
        message: actualError.message,
        stack: actualError.stack,
      },
    })
  }

  return logger
}

export function getPrefix(screenUrl: string | null | undefined) {
  if (isEmpty(screenUrl)) {
    return ''
  }

  return screenUrl
    .split('/')
    ?.filter((name) => !isEmpty(name))
    ?.map((name) => `[${name.toUpperCase()}]`)
    ?.join('')
}
